import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ProfileMediaRenderDots from './ProfileMediaRenderDots';
import { useProfile } from '../../contexts/ProfileContext';
import { useTransition, animated } from '@react-spring/web';
import { DotsAnimatedWrapper, DotsWrapper } from '../../components/common/CommonStyle';

const MediaCounterContainer = styled('div')({
  position: 'absolute',
  height: 38,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  bottom: 0,
  zIndex: 2,
});

const CounterWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

interface ProfileMediaCounterProps {
  photos: any;
  currentSlide: number;
}

const ProfileMediaCounter = ({ photos, currentSlide }: ProfileMediaCounterProps): ReactElement => {
  const { isDotsAnimated, animateDirection } = useProfile();
  const dotsRef = useRef([]);
  const [ease, setEase] = useState({ x1: 0 });
  const [applyAnimation, setApplyAnimation] = useState(false);

  useEffect(() => {
    if (currentSlide === 0) {
      setApplyAnimation(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (photos.length === 2) {
      const x1 = dotsRef?.current[0]?.getBoundingClientRect().x;

      setEase({ x1: x1 });
    } else {
      const nextReft =
        animateDirection === 'left' ? dotsRef?.current[currentSlide + 1] : dotsRef?.current[currentSlide];
      const currentRef = dotsRef?.current[currentSlide];
      const x1 =
        animateDirection === 'left' ? nextReft?.getBoundingClientRect().x : currentRef?.getBoundingClientRect().x;

      setEase({ x1 });
    }

    setApplyAnimation(!(currentSlide === 0 && !isDotsAnimated));
  }, [photos, currentSlide, dotsRef?.current[currentSlide], isDotsAnimated, animateDirection]);

  const transitions = useTransition(currentSlide, {
    keys: currentSlide,
    from: {
      opacity: 1,
      transform: 'scaleX(1)',
    },
    enter: {
      opacity: 0,
      transform: 'scaleX(0)',
    },
    delay: 300,
  });

  const renderAnimatedDiv = () => {
    let diffX = Math.abs(ease.x1 - 12);

    if (photos.length === 2) {
      diffX = Math.abs(ease.x1);
    }

    return transitions(styles => (
      <DotsAnimatedWrapper>
        <animated.div
          style={{
            ...styles,
            position: 'absolute',
            height: 6,
            backgroundColor: 'white',
            borderRadius: 3,
            visibility: isDotsAnimated && applyAnimation ? 'visible' : 'hidden',
            top: '22px',
            left: `${diffX + 'px'}`,
            transformOrigin: animateDirection === 'left' ? '0% 100%' : '100% 0%',
            width: 19,
            transition: 'transform 0.3s ease-out forwards',
          }}
        />
      </DotsAnimatedWrapper>
    ));
  };

  return (
    <MediaCounterContainer>
      <CounterWrapper>
        <DotsWrapper>
          {photos.map((item, index) => {
            return (
              <ProfileMediaRenderDots
                ref={element => {
                  dotsRef.current[index] = element;
                }}
                key={index}
                item={item}
                index={index}
                currentSlide={currentSlide}
                photos={photos}
              />
            );
          })}
        </DotsWrapper>
        {renderAnimatedDiv()}
      </CounterWrapper>
    </MediaCounterContainer>
  );
};

export default ProfileMediaCounter;
